import { useState, useCallback, useRef } from 'react';

/**
 * Hook für Mausposition-Tracking mit optimierter Performance.
 * Verwendet einen Ref für schnelle synchrone Abfragen und
 * einen State für UI-Updates mit Throttling.
 */
export function useMousePosition() {
  // State für UI (throttled aktualisiert)
  const [mousePosition, setMousePositionState] = useState({ x: 0, y: 0 });

  // Ref für schnelle synchrone Abfragen (ohne State-Overhead)
  const mousePosRef = useRef({ x: 0, y: 0 });
  const lastSyncedRef = useRef({ x: 0, y: 0 });
  const mouseSyncScheduledRef = useRef(false);

  // Schedule Mouse Position Sync - verwendet setTimeout statt RAF
  // RAF kann während R3F Render laufen, setTimeout garantiert Ausführung danach
  const scheduleMouseSync = useCallback(() => {
    if (mouseSyncScheduledRef.current) return;

    const current = mousePosRef.current;
    const last = lastSyncedRef.current;

    // Nur syncen wenn sich was geändert hat
    if (current.x === last.x && current.y === last.y) {
      return;
    }

    mouseSyncScheduledRef.current = true;

    // setTimeout mit 16ms (1 Frame) für stabiles Verhalten
    setTimeout(() => {
      mouseSyncScheduledRef.current = false;

      const pos = mousePosRef.current;

      // Nochmal prüfen ob sich wirklich was geändert hat
      if (pos.x === lastSyncedRef.current.x && pos.y === lastSyncedRef.current.y) {
        return;
      }

      lastSyncedRef.current = { x: pos.x, y: pos.y };
      setMousePositionState({ x: pos.x, y: pos.y });
    }, 16);
  }, []);

  // Update-Funktion: Aktualisiert Ref und scheduled State-Sync
  // WICHTIG: Diese Funktion wird aus useFrame aufgerufen (R3F Render-Loop)
  const setMousePosition = useCallback((pos) => {
    // Ref immer sofort aktualisieren (für synchrone Abfragen)
    mousePosRef.current = pos;
    scheduleMouseSync();
  }, [scheduleMouseSync]);

  // Getter für Ref (für direkte Abfrage ohne State-Update)
  const getMousePosition = useCallback(() => mousePosRef.current, []);

  return {
    mousePosition,      // State für UI (throttled)
    setMousePosition,   // Für useFrame - aktualisiert Ref
    getMousePosition,   // Synchroner Getter für Ref
  };
}

export default useMousePosition;
